import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@lxp-app/store/store";

import Input from "@lxp-app/components/atoms/input";
import ButtonGroup from "@lxp-app/components/atoms/buttongroup";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "@mui/material";

import { UploadConfigPayload } from "./types/question-type";

import { QuestionPreviewMapping } from "./question-utils/questionPrevMapping";
import { NewQuestionStateSliceActions } from "./slice/newQuestionStateSlice";
import { getAuthoringLibraryLabel } from "../../utils/authoringLibraryResources/localizeAuthoringLibraryLabels";
import { useAuthoringLibraryLanguage } from "../../utils/authoringLibraryResources/useAuthoringLibraryLanguage";

interface ConfigDataInterface {
  configData: UploadConfigPayload | null;
}

// practice allowed will always be true and disabled.
type fields =
  | "subtype"
  | "type"
  | "allowedFileType"
  | "maxUpload"
  | "maxDuration"
  | "minDuration"
  | "uploadLimit"
  | "practiceAllowed";

export const initialConfigData: UploadConfigPayload = {
  title: "This is a text question",
  subType: "text",
  type: "upload",
  maxUploadSize: 1,
  allowedFileTypes: ["txt", "mp4", "mp3", "webm"],
  uploadLimit: 2,
  solution: [""],
  minUploadDuration: 0.5,
  maxUploadDuration: 1,
  id: "",
  questionId: "",
  allowPractice: true,
  archived: false,
  orderInQuestion: 1,
  version: 1
};

const UploadConfig = ({ configData }: ConfigDataInterface) => {
  const dispatch = useDispatch();
  const profileLanguage = useSelector(
    (state: RootState) => state.UserInfoSlice.profile?.preferences?.language
  );
  const { language } = useAuthoringLibraryLanguage(profileLanguage);

  const infoMessage = {
    maxDuration: getAuthoringLibraryLabel("maxDurationInfo", language),
    minDuration: getAuthoringLibraryLabel("minDurationInfo", language),
    uploadLimit: getAuthoringLibraryLabel("uploadLimitInfo", language)
  };

  const practiceAllowed = [
    {
      label: getAuthoringLibraryLabel("yes", language),
      value: true,
      disable: true
    },
    {
      label: getAuthoringLibraryLabel("no", language),
      value: false,
      disable: true
    }
  ];

  const updatedConfigData = useSelector(
    (state: RootState) => state.NewQuestionStateSlice.uploadConfigData
  );

  const questionPreviewData = useSelector(
    (state: RootState) => state.QuestionPreviewSlice.DataMapping
  );

  useEffect(() => {
    if (configData === null) return;
    if (configData) {
      dispatch(NewQuestionStateSliceActions.setUploadConfigData(configData));
      QuestionPreviewMapping(
        dispatch,
        {
          id: configData.questionId,
          data: {
            UploadConfigData: configData
          }
        },
        true
      );
    }
  }, [configData]);

  const getTitle = (subType: any, type: any): string => {
    if (subType === "text")
      return getAuthoringLibraryLabel("textQuestionTitle", language);
    if (subType === "audio") {
      return type === "record"
        ? getAuthoringLibraryLabel("audioRecordQuestionTitle", language)
        : getAuthoringLibraryLabel("audioUploadQuestionTitle", language);
    }
    if (subType === "video") {
      return type === "record"
        ? getAuthoringLibraryLabel("videoRecordQuestionTitle", language)
        : getAuthoringLibraryLabel("videoUploadQuestionTitle", language);
    }
    return getAuthoringLibraryLabel(
      "subjectiveQuestionUploadConfigTitle",
      language
    );
  };

  const handleFieldUpdate = (field: fields, value: any) => {
    const previewData = questionPreviewData[updatedConfigData.questionId];

    switch (field) {
      // handling type and title here only.
      case "subtype": {
        const updatedData = {
          ...updatedConfigData,
          subType: value,
          title: getTitle(value, updatedConfigData.type),
          type: value === "text" ? "upload" : "record"
        };

        dispatch(NewQuestionStateSliceActions.setUploadConfigData(updatedData));

        QuestionPreviewMapping(
          dispatch,
          {
            id: updatedConfigData.questionId,
            data: {
              ...previewData,
              uploadconfigdata: updatedData
            }
          },
          true
        );
        break;
      }
      case "maxUpload": {
        const updatedData = {
          ...updatedConfigData,
          maxUploadSize: +value
        };
        dispatch(NewQuestionStateSliceActions.setUploadConfigData(updatedData));
        QuestionPreviewMapping(
          dispatch,
          {
            id: updatedConfigData.questionId,
            data: {
              ...previewData,
              uploadconfigdata: updatedData
            }
          },
          true
        );
        break;
      }
      case "minDuration": {
        const updatedData = {
          ...updatedConfigData,
          minUploadDuration: +value
        };
        dispatch(NewQuestionStateSliceActions.setUploadConfigData(updatedData));
        QuestionPreviewMapping(
          dispatch,
          {
            id: updatedConfigData.questionId,
            data: {
              ...previewData,
              uploadconfigdata: updatedData
            }
          },
          true
        );
        break;
      }
      case "maxDuration": {
        const updatedData = {
          ...updatedConfigData,
          maxUploadDuration: +value
        };
        dispatch(NewQuestionStateSliceActions.setUploadConfigData(updatedData));
        QuestionPreviewMapping(
          dispatch,
          {
            id: updatedConfigData.questionId,
            data: {
              ...previewData,
              uploadconfigdata: updatedData
            }
          },
          true
        );
        break;
      }
      case "uploadLimit": {
        if (value === "0") {
          return;
        }

        const updatedData = {
          ...updatedConfigData,
          uploadLimit: value === "" ? "" : parseInt(value, 10)
        };
        dispatch(NewQuestionStateSliceActions.setUploadConfigData(updatedData));
        QuestionPreviewMapping(
          dispatch,
          {
            id: updatedConfigData.questionId,
            data: {
              ...previewData,
              uploadconfigdata: updatedData
            }
          },
          true
        );
        break;
      }
      case "practiceAllowed": {
        const updatedData = {
          ...updatedConfigData,
          allowPractice: value === "true"
        };

        dispatch(NewQuestionStateSliceActions.setUploadConfigData(updatedData));
        QuestionPreviewMapping(
          dispatch,
          {
            id: updatedConfigData.questionId,
            data: {
              ...previewData,
              uploadconfigdata: updatedData
            }
          },
          true
        );
        break;
      }
    }
  };

  return (
    <div>
      <div className="flex gap-5 w-full items-start">
        <div className="w-1/2 flex gap-x-2 items-center">
          {" "}
          <label className="text-secondary mb-1 font-medium">
            {getAuthoringLibraryLabel("recordingLimitInMinutes", language)}
          </label>
          <Tooltip title={infoMessage.maxDuration}>
            <div className="w-5 h-5">
              <InformationCircleIcon />
            </div>
          </Tooltip>
        </div>
        <div className="w-1/2 flex gap-x-2 items-center">
          {" "}
          <label className="text-secondary mb-1 font-medium">
            {getAuthoringLibraryLabel(
              "minimumResponseDurationInMinutes",
              language
            )}
          </label>
          <Tooltip title={infoMessage.minDuration}>
            <div className="w-5 h-5">
              <InformationCircleIcon />
            </div>
          </Tooltip>
        </div>
      </div>
      <div className="flex justify-between gap-5 w-full">
        <div className="w-full mb-5">
          <Input
            name="maxDuration"
            value={updatedConfigData.maxUploadDuration}
            onChange={(e) => {
              handleFieldUpdate("maxDuration", e.target.value);
            }}
            type="number"
            min={1}
            max={20}
            error={
              updatedConfigData.maxUploadDuration < 1 ||
              updatedConfigData.maxUploadDuration > 20
            }
            errorMessage={
              updatedConfigData.maxUploadDuration > 20
                ? getAuthoringLibraryLabel(
                    "recordingLimitLessThanTwenty",
                    language
                  )
                : getAuthoringLibraryLabel(
                    "recordingLimitGreaterThanOrEqualOne",
                    language
                  )
            }
          />
        </div>
        <div className=" w-full mb-5">
          <Input
            name="minDuration"
            decimalAllowed={true}
            stepValue={0.5}
            value={updatedConfigData.minUploadDuration}
            onChange={(e) => {
              let value = e.target.value;

              if (value === "") {
                handleFieldUpdate("minDuration", value);
                return;
              }

              const num = parseFloat(value);
              if (!isNaN(num)) {
                value = num.toFixed(1);
                handleFieldUpdate("minDuration", value);
              }
            }}
            type="number"
            min={0.5}
            max={20}
            hideDigitLimitError={
              updatedConfigData.minUploadDuration >
              updatedConfigData.maxUploadDuration
            }
            error={
              updatedConfigData.minUploadDuration < 0.5 ||
              updatedConfigData.minUploadDuration >
                updatedConfigData.maxUploadDuration
            }
            errorMessage={
              updatedConfigData.minUploadDuration >
              updatedConfigData.maxUploadDuration
                ? getAuthoringLibraryLabel(
                    "minimumDurationLessThanRecordingLimit",
                    language
                  )
                : getAuthoringLibraryLabel(
                    "durationGreaterThanPointFive",
                    language
                  )
            }
          />
        </div>
      </div>
      <div className="flex gap-5 w-full">
        <div className="w-1/2 flex gap-x-2 items-center">
          {" "}
          <label className="text-secondary mb-1 font-medium">
            {getAuthoringLibraryLabel("uploadLimit", language)}
          </label>
          <Tooltip title={infoMessage.uploadLimit}>
            <div className="w-5 h-5">
              <InformationCircleIcon />
            </div>
          </Tooltip>
        </div>
        <div className="w-1/2 flex gap-x-2 items-center">
          {" "}
          <label className="text-secondary mb-1 font-medium">
            {getAuthoringLibraryLabel("practiceAllowed", language)}
          </label>
        </div>
      </div>
      <div className="flex justify-between gap-5">
        <div className=" w-full mb-2 text-base">
          <Input
            name="minDuration"
            value={updatedConfigData.uploadLimit}
            onChange={(e) => {
              handleFieldUpdate("uploadLimit", e.target.value);
            }}
            type="number"
            min={1}
            max={5}
            error={updatedConfigData.uploadLimit <= 0}
            errorMessage={getAuthoringLibraryLabel(
              "uploadLimitGreaterThanZero",
              language
            )}
          />
        </div>
        <div className=" w-full mb-2 text-base">
          <ButtonGroup
            buttons={practiceAllowed}
            defaultValue={0}
            setterFunction={(e) => {
              handleFieldUpdate("practiceAllowed", e.target.value);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default UploadConfig;
